import { useMemo } from 'react'
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  List,
  Loader,
  Popover,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'

import {
  IconAlertTriangle,
  IconDeviceFloppy,
  IconHelpCircle,
} from '@tabler/icons-react'

import Device, {
  DeviceIDTypes,
  Valve,
  ValveSettingsPatch,
  VALVE_RECOVERY_POLICIES,
  VALVE_RECOVERY_POLICY_META,
} from '@data/device/model/device'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { describeApiError } from '@common/utils/apiErrors'
import { useValveSettings } from '@data/device/device'
import { applyValveSettings } from '@data/device/mapDevice'
import {
  useDeviceRefresh,
  type ValveTarget,
} from '@data/device/device'
import { TokenPermission } from '@data/rbac/model/rbac'
import { useValveSettingsDrafts } from './useValveSettingsDrafts'
import { useValveRecoveryPolicy } from './useValveRecoveryPolicy'
import { useValveCommissioning } from './useValveCommissioning'
import { ValveRecoveryPanel } from './ValveRecoveryPanel'
import { CommissionValvesPanel } from './CommissionValvesPanel'
import { ValveStatsGrid } from './ValveStatsGrid'
import { ValveIdentitySettings } from './ValveIdentitySettings'
import { ValveAdvancedData } from './ValveAdvancedData'

export interface DeviceValvesTabProps {
  device: Device
  permissions: TokenPermission[]
  deviceUpdating: boolean
  isActive: boolean
}

// Mantine Select works in strings; the wire format is an integer.
export const RECOVERY_POLICY_OPTIONS = VALVE_RECOVERY_POLICIES.map((policy) => ({
  value: String(policy),
  label: VALVE_RECOVERY_POLICY_META[policy].label,
}))

const COMMISSION_VALVES_STEPS = [
  'Every valve is opened first, to put everything in a known starting position.',
  'Every valve is then closed, and the hub checks which ones actually confirmed as '
  + 'closed — any that didn\'t respond are marked as not connected.',
  'Every valve is opened again, and the same check happens the other way — any that '
  + 'didn\'t confirm open are marked not connected.',
  'While all this is happening, the hub also times how long each valve took to move. It '
  + 'uses that timing to work out whether each valve is a small or large type, and '
  + 'automatically picks the right way to recover its position later if it\'s ever '
  + 'uncertain — small valves get a gentle back-and-forth approach, large valves get a '
  + '"restore to where it was" approach — unless someone has already told the hub '
  + 'explicitly which approach to use.',
  'At the end, the hub reports whether each valve passed, and shows what happened in each '
  + 'direction separately, so a valve with a fault that only shows up when opening (or '
  + 'only when closing) doesn\'t get missed.',
]

export const CommissionValvesHelp = () => (
  <Popover
    width={420}
    position='bottom-start'
    shadow='md'
    withArrow
    middlewares={{
      shift: true,
      flip: true,
      size: { padding: 8 },
    }}
  >
    <Popover.Target>
      <ActionIcon
        variant='subtle'
        color='gray'
        size='sm'
        aria-label='About commissioning valves'
      >
        <IconHelpCircle size={16} />
      </ActionIcon>
    </Popover.Target>

    <Popover.Dropdown style={{ overflowY: 'auto' }}>
      <Stack gap='sm'>
        <Text size='sm'>
          A setup process that teaches the hub which valves are actually wired up and
          working, and figures out the right way to look after each one automatically.
        </Text>

        <Text size='sm' fw={600}>
          What happens, step by step
        </Text>

        <List type='ordered' size='sm' spacing={2}>
          {COMMISSION_VALVES_STEPS.map((step) => (
            <List.Item key={step}>
              {step}
            </List.Item>
          ))}
        </List>

        <Text size='xs' c='dimmed'>
          Roughly a minute to a minute and a half in total, since each valve needs enough
          time to fully open or close before the hub checks on it — slower for larger
          valves. If commissioning is already running, starting it again is simply
          ignored; it has to finish first.
        </Text>

        <Alert
          color='red'
          variant='light'
          icon={<IconAlertTriangle size={16} />}
        >
          The sequence drives every valve open, then closed, then open again — it will run
          for approximately 90 seconds. All other device actions are disabled while it
          runs. Data will auto-refresh once it completes.
        </Alert>
      </Stack>
    </Popover.Dropdown>
  </Popover>
)

export const ValveProbeHelp = () => (
  <Popover
    width={420}
    position='bottom-start'
    shadow='md'
    withArrow
    middlewares={{
      shift: true,
      flip: true,
      size: { padding: 8 },
    }}
  >
    <Popover.Target>
      <ActionIcon
        variant='subtle'
        color='gray'
        size='sm'
        aria-label='About the valve probe'
      >
        <IconHelpCircle size={16} />
      </ActionIcon>
    </Popover.Target>

    <Popover.Dropdown
      style={{ overflowY: 'auto' }}
    >
      <Stack gap='sm'>
        <Text size='md'>
          A recovery action for a valve whose position cannot be determined. The device
          cannot read position passively — feedback is powered through the coil drive line,
          so a valve sitting de-energised reports an indeterminate position wherever it
          actually is. The probe re-energises each drive line in turn, reads the feedback,
          corrects the stored position, and leaves the line held so it stays readable.
        </Text>

        <Text size='sm' fw={600}>
          Reach for it when
        </Text>

        <Stack gap={2}>
          <Text size='sm'>
            • A valve reports an indeterminate position when it should be at a known one
          </Text>
          <Text size='sm'>
            • A valve shows Operation Failed after a wiring fault, jam, or ceiling event
          </Text>
          <Text size='sm'>
            • A valve may have been moved by hand while the device was unpowered
          </Text>
        </Stack>

        <Text size='md' fw={600}>
          Outcomes, per valve
        </Text>

        <Stack gap={2}>
          <Text size='sm'>
            <b>Confirmed</b> — position and state agree; nothing moved.
          </Text>
          <Text size='sm'>
            <b>Adopted</b> — they agree but differ from before; the valve had moved while
            unpowered and the device adopted physical reality. Re-commanding it is your call.
          </Text>
          <Text size='sm'>
            <b>Unknown</b> — position still indeterminate; feedback fault or mechanically
            stuck. Needs an engineer.
          </Text>
        </Stack>

        <Text size='sm' c='dimmed'>
          This is not a read. It blocks the device for up to 9 seconds, energises drive lines
          and can physically move a valve, and is refused while any valve is mid-travel or
          commissioning is active. The same routine runs automatically at boot.
        </Text>
      </Stack>
    </Popover.Dropdown>
  </Popover>
)

// Popover, not HoverCard — this opens on click and stays open until dismissed, so the
// reference text can be read without holding the pointer in place.
export const RecoveryModeHelp = () => (
  <Popover
    width={420}
    position='bottom-start'
    shadow='md'
    withArrow
    middlewares={{
      shift: true,
      flip: true,
      size: { padding: 8 },
    }}
  >
    <Popover.Target>
      <ActionIcon
        variant='subtle'
        color='gray'
        size='sm'
        aria-label='About valve recovery modes'
      >
        <IconHelpCircle size={16} />
      </ActionIcon>
    </Popover.Target>

    <Popover.Dropdown style={{ overflowY: 'auto' }}>
      <Stack gap='sm'>
        <Text size='sm'>
          Governs what a valve does when its position cannot be confirmed.
        </Text>

        {VALVE_RECOVERY_POLICIES.map((policy) => {
          const meta = VALVE_RECOVERY_POLICY_META[policy]

          return (
            <Stack key={policy} gap={2}>
              <Group gap='sm' wrap='nowrap'>
                {/*<Badge size='sm' variant='light' color='gray'>*/}
                {/*  {policy}*/}
                {/*</Badge>*/}

                <Text size='md' fw={600}>
                  {meta.label}
                </Text>
              </Group>

              {/*<Text size='sm' c='dimmed' ff='monospace'>*/}
              {/*  {meta.name}*/}
              {/*</Text>*/}

              <Text size='sm'>
                {meta.description}
              </Text>

              <List size='sm' spacing={2}>
                {meta.points.map((point) => (
                  <List.Item key={point}>
                    {point}
                  </List.Item>
                ))}
              </List>
            </Stack>
          )
        })}

        <Text size='sm' c='dimmed'>
          Changes reach the device over Azure IoT rather than the database, so saving is
          slower than other settings and fails if the device is unreachable.
        </Text>
      </Stack>
    </Popover.Dropdown>
  </Popover>
)

interface ValveCardProps {
  valve: Valve
  device: Device
  draft?: ValveSettingsPatch
  onSettingsChange: (valve: Valve, patch: ValveSettingsPatch) => void
  isSaving?: boolean
  isSettingsUnavailable?: boolean
}

const ValveCard = ({
  valve,
  device,
  draft,
  onSettingsChange,
  isSaving = false,
  isSettingsUnavailable = false,
}: ValveCardProps) => (
  <Card withBorder radius='md' p='lg'>
    <Stack gap='md'>
      <ValveIdentitySettings
        valve={valve}
        device={device}
        draft={draft}
        onSettingsChange={onSettingsChange}
        isSaving={isSaving}
        isSettingsUnavailable={isSettingsUnavailable}
      />
    </Stack>
  </Card>
)

export const DeviceValvesTab = ({
  device,
  permissions,
  deviceUpdating,
  isActive,
}: DeviceValvesTabProps) => {
  const canReadValveSettings = hasPermissions(permissions, [
    'READ:DEVICE:PROD',
  ])
  const {
    data: valveSettings,
    error: valveSettingsError,
    isLoading: isValveSettingsLoading,
  } = useValveSettings(device.dUuid, isActive && canReadValveSettings)
  const valveDevice = useMemo(
    () => ({
      ...device,
      valves: valveSettings
        ? applyValveSettings(device.valves, valveSettings)
        : device.valves,
    }),
    [device, valveSettings],
  )
  const valves = Object.values(valveDevice.valves || {})
  const isSettingsUnavailable =
    isValveSettingsLoading || !!valveSettingsError || !canReadValveSettings
  const { mutateAsync: loadDevice } = useDeviceRefresh()
  const valveTarget = useMemo<ValveTarget>(
    () => ({
      kind: 'production',
      dUuid: device.dUuid,
    }),
    [device.dUuid],
  )
  const {
    cooldown,
    isSending: deviceSendingMessage,
    handleCommissionValves,
  } = useValveCommissioning(valveTarget, () => {
    void loadDevice({
      deviceId: device.dUuid,
      idType: DeviceIDTypes.dUuid,
    })
  })

  const {
    drafts,
    modifiedCount,
    isSavingValves,
    handleSettingsChange,
    handleDiscardValves,
    handleSaveValves,
  } = useValveSettingsDrafts(device.dUuid)

  const {
    selectedPolicy,
    isPolicyModified,
    isSavingPolicy,
    handlePolicyChange,
    handleDiscardPolicy,
    handleSavePolicy,
    probeConfirming,
    setProbeConfirming,
    handleProbe,
    isProbing,
    isFirmwareSupported,
    minFirmwareVersion,
  } = useValveRecoveryPolicy(valveTarget, device.valveRecovery, device.firmware)

  const hasValveCommPermission = hasPermissions(permissions, [
    'DEVICE:VALVE:COMM',
  ])

  const canCommissionValves =
    device.deviceVendor === 'SMARTFLOW' &&
    hasValveCommPermission

  return (
    <Stack gap='lg'>
      <ValveStatsGrid valves={valves} />

      <Card withBorder p='md'>
        <Stack gap='md'>
          <Group justify='space-between'>
            <Group gap='xs'>
              <Text fw={600}>
                Valves
              </Text>

              {isValveSettingsLoading && <Loader size='xs' />}
            </Group>

            {hasValveCommPermission && (
              <ValveAdvancedData target={valveTarget} firmware={device.firmware} />
            )}
          </Group>

          <SimpleGrid
            cols={{ base: 1, sm: 2, xl: 3 }}
          >
            {valves.map((valve) => (
              <ValveCard
                key={valve.key}
                valve={valve}
                device={valveDevice}
                draft={drafts[valve.key]}
                onSettingsChange={handleSettingsChange}
                isSaving={isSavingValves}
                isSettingsUnavailable={isSettingsUnavailable}
              />
            ))}
          </SimpleGrid>

          {valveSettingsError && (
            <Alert color='red'>
              {describeApiError(valveSettingsError, 'Unable to load valve settings.')}
            </Alert>
          )}

          <Group justify='flex-end' gap='sm'>
            <Button
              variant='default'
              onClick={handleDiscardValves}
              disabled={!modifiedCount || isSavingValves}
            >
              Discard
            </Button>

            <Button
              onClick={handleSaveValves}
              disabled={!modifiedCount || isSettingsUnavailable}
              loading={isSavingValves}
              leftSection={<IconDeviceFloppy size={16} />}
            >
              {modifiedCount
                ? `Save changes (${modifiedCount})`
                : 'Save changes'}
            </Button>
          </Group>
        </Stack>
      </Card>

      {hasValveCommPermission && (
        <ValveRecoveryPanel
          selectedPolicy={selectedPolicy}
          isPolicyModified={isPolicyModified}
          isSavingPolicy={isSavingPolicy}
          onPolicyChange={handlePolicyChange}
          onDiscardPolicy={handleDiscardPolicy}
          onSavePolicy={handleSavePolicy}
          probeConfirming={probeConfirming}
          onProbeConfirmingChange={setProbeConfirming}
          onProbe={handleProbe}
          isProbing={isProbing}
          isFirmwareSupported={isFirmwareSupported}
          minFirmwareVersion={minFirmwareVersion}
        />
      )}

      {canCommissionValves && (
        <CommissionValvesPanel
          onCommission={handleCommissionValves}
          isSending={deviceSendingMessage}
          disabled={deviceUpdating || cooldown > 0}
          cooldown={cooldown}
        />
      )}
    </Stack>
  )
}
